<script setup lang="ts">
import { useFirebase } from "@/composables/useFirebase";

import { useAssistantSettingsStore } from "@/stores/assistantSettingsStore";

const { assistantSettings } = useAssistantSettingsStore();

const {
  isPanelExpanded,
  showClosePanelConfirmation,
  minimizePanel,
  expandPanel,
  collapsePanel,
} = usePanel();

const { logEvent } = useFirebase();

const logoImage = computed(() => {
  return lightOrDark(assistantSettings.theme.main_body_background_color) ===
    "dark"
    ? assistantSettings.images.logo_light
    : assistantSettings.images.logo_dark;
});

const openUrl = async (url: string, external: boolean = false) => {
  // Log event to firebase
  logEvent("open_menu_item");
  // Open url
  window.open(url, external ? "_blank" : "_self");
};
</script>

<template>
  <header class="layout-header">
    <div class="section is-left">
      <Dropdown v-if="assistantSettings.menu.items.length">
        <DropdownButton>
          <div class="button is-info">
            <BaseIcon name="more_horiz" class="button-icon" />
          </div>
        </DropdownButton>
        <DropdownMenu>
          <DropdownMenuItem
            v-for="(item, index) in assistantSettings.menu.items"
            :key="index"
            external
            @click="openUrl(item.url, true)"
          >
            {{ item.label }}
          </DropdownMenuItem>
        </DropdownMenu>
      </Dropdown>
    </div>
    <div class="section is-middle">
      <div class="logo">
        <!-- <img src="@/assets/images/logo-main.png" /> -->
        <img :src="logoImage" />
      </div>
    </div>
    <div class="section is-right">
      <div class="button is-minimize">
        <BaseIcon
          name="horizontal_rule"
          class="button-icon"
          @click="minimizePanel"
        />
      </div>
      <div class="button is-expand-collapse">
        <BaseIcon
          v-if="isPanelExpanded"
          name="fullscreen_exit"
          class="button-icon"
          @click="collapsePanel"
        />
        <BaseIcon
          v-else
          name="fullscreen"
          class="button-icon"
          @click="expandPanel"
        />
      </div>

      <div class="button is-close">
        <BaseIcon
          name="close"
          class="button-icon"
          @click="showClosePanelConfirmation"
        />
      </div>
    </div>
  </header>
</template>

<style lang="scss" scoped>
.layout-header {
  position: relative;
  z-index: 1;
  display: flex;
  height: 60px;
  border-bottom: solid 1px rgba(0, 0, 0, 0.1);
  background-color: var(--main-body-background-color);
  backdrop-filter: blur(5px);
  padding-top: env(safe-area-inset-top);
  // box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.1);
}

.section {
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;

  &.is-left,
  &.is-right {
    padding: 20px;
    gap: 6px;
  }

  &.is-left {
    justify-content: flex-start;
    margin-right: auto;
  }

  // &.is-middle {
  //   flex: 1 0 auto;
  // }

  &.is-right {
    justify-content: flex-end;
    margin-left: auto;
  }
}

.logo {
  flex-grow: 1;
  width: 140px;
  text-align: center;
  cursor: pointer;

  img {
    max-width: 140px;
    max-height: 35px;
    object-fit: contain;
  }
}

.button {
  cursor: pointer;

  &:hover {
    color: var(--main-button-background-color);
  }
}

.button-icon {
  font-size: 1.3rem;
}

@media only screen and (max-width: 420px) {
  .section {
    &.is-left,
    &.is-right {
      padding: 20px;
      gap: 15px;
    }
  }

  .button {
    &.is-expand-collapse {
      display: none;
    }
  }
}
</style>
